import { ReactNode, useMemo } from "react";
import {
	Alert,
	AlertProps,
	createTheme,
	SvgIcon,
	ThemeOptions,
	ThemeProvider,
	Typography,
	useTheme,
} from "@mui/material";
import InfoMegaphone from "./images/info.svg?react";
import WarningTriangle from "./images/warning.svg?react";

export type CoreAlertProps = Omit<AlertProps, "variant" | "severity" | "title"> & {
	severity: "info" | "warning";
	title: ReactNode;
	boxShadow?: boolean;
	size?: "small" | "large";
};

const outlinedAlertStyles = {
	color: "black",
	borderRadius: 8,
	alignItems: "center",
	padding: "15px",
	"& .MuiAlert-icon": {
		alignItems: "center",
		padding: 0,
		opacity: 1,
	},
	"& .MuiAlert-icon svg": {
		width: 24,
		height: 24,
	},
	"& .MuiAlert-message": {
		padding: 0,
	},
};

const alertThemeOptions: ThemeOptions = {
	components: {
		MuiAlert: {
			variants: [
				{
					props: { variant: "outlined", severity: "warning" },
					style: {
						...outlinedAlertStyles,
						backgroundColor: "rgba(189, 135, 0, 0.05)",
						borderColor: "#BD8700",
						"& .MuiAlert-icon": {
							...outlinedAlertStyles["& .MuiAlert-icon"],
							color: "#BD8700",
						},
					},
				},
				{
					props: { variant: "outlined", severity: "info" },
					style: {
						...outlinedAlertStyles,
						backgroundColor: "#EEFBFE",
						borderColor: "#0B6F85",
						"& .MuiAlert-icon": {
							...outlinedAlertStyles["& .MuiAlert-icon"],
							color: "#0B6F85",
						},
					},
				},
			],
		},
	},
};

export default function CoreAlert({
	boxShadow = false,
	size = "small",
	title,
	severity,
	iconMapping = {
		info: (
			<SvgIcon>
				<InfoMegaphone />
			</SvgIcon>
		),
		warning: (
			<SvgIcon>
				<WarningTriangle />
			</SvgIcon>
		),
	},
	sx,
	children,
	...alertProps
}: CoreAlertProps) {
	const baseTheme = useTheme();
	const theme = useMemo(() => createTheme(baseTheme, alertThemeOptions), [baseTheme]);

	const fontSize = size === "small" ? 13 : 16;

	return (
		<ThemeProvider theme={theme}>
			<Alert
				{...alertProps}
				variant="outlined"
				severity={severity}
				iconMapping={iconMapping}
				sx={{
					borderWidth: boxShadow ? 1 : 2,
					boxShadow: boxShadow ? "0 0 4px 0 #00000040" : undefined,
					fontSize,
					...sx,
				}}
			>
				<Typography component="span" sx={{ fontSize, fontWeight: 700, mr: "5px" }}>
					{title}:
				</Typography>
				{children}
			</Alert>
		</ThemeProvider>
	);
}
